import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import {
  getUserInitials,
  getUserName,
  maybe,
  renderCollection
} from "@saleor/misc";
import { StaffListUrlSortField } from "@saleor/staff/urls";
import { ListProps, SortPage } from "@saleor/types";
import { MfaStatus } from "@saleor/types/globalTypes";
import { getStaffStatus, getStaffUserType } from "@saleor/utils/smop";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { StaffList_staffUsers_edges_node } from "../../types/StaffList";

const useStyles = makeStyles(
  theme => ({
    avatar: {
      alignItems: "center",
      borderRadius: "100%",
      display: "grid",
      float: "left",
      height: 47,
      justifyContent: "center",
      marginRight: theme.spacing(1),
      overflow: "hidden",
      width: 47
    },
    avatarDefault: {
      "& div": {
        color: theme.palette.primary.contrastText,
        lineHeight: "47px"
      },
      background: theme.palette.primary.main,
      height: 47,
      textAlign: "center",
      width: 47
    },
    avatarImage: {
      pointerEvents: "none",
      width: "100%"
    },
    colEmail: {
      width: "40%"
    },
    colMFAStatus: {
      width: "20%"
    },
    colStatus: {
      width: "20%"
    },
    colUserType: {
      width: "20%"
    },
    statusExpired: {
      backgroundColor: "#FEE4ED"
    },
    statusExpiredLabel: {
      color: "#C15669",
      fontWeight: 600,
      overflow: "hidden",
      padding: "0px 11px 0px 11px",
      textOverflow: "ellipsis"
    },
    statusPending: {
      backgroundColor: "#FFF6DE"
    },
    statusPendingLabel: {
      color: "#82570A",
      fontWeight: 600,
      overflow: "hidden",
      padding: "0px 11px 0px 11px",
      textOverflow: "ellipsis"
    },
    statusText: {
      color: "#9E9D9D"
    },
    statusVerified: {
      backgroundColor: "#DEF8FA"
    },
    statusVerifiedLabel: {
      color: "#246C63",
      fontWeight: 600,
      overflow: "hidden",
      padding: "0px 11px 0px 11px",
      textOverflow: "ellipsis"
    },
    statusWrapper: {
      borderRadius: "25px",
      display: "inline",
      margin: "2px",
      paddingBottom: "5px",
      paddingTop: "5px",
      position: "relative",
      whiteSpace: "nowrap"
    },
    tableRow: {
      cursor: "pointer"
    },
    wideColumn: {
      width: "30%"
    }
  }),
  { name: "StaffList" }
);

interface StaffListProps extends ListProps, SortPage<StaffListUrlSortField> {
  staffMembers: StaffList_staffUsers_edges_node[];
}

const numberOfColumns = 5;

const StaffList: React.FC<StaffListProps> = props => {
  const {
    settings,
    disabled,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort,
    pageInfo,
    sort,
    staffMembers
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  return (
    <ResponsiveTable>
      <colgroup>
        <col />
        <col className={classes.colMFAStatus} />
        <col className={classes.colEmail} />
        <col className={classes.colUserType} />
        <col className={classes.colStatus} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCellHeader
            direction={
              sort.sort === StaffListUrlSortField.name
                ? getArrowDirection(sort.asc)
                : undefined
            }
            arrowPosition="right"
            onClick={() => onSort(StaffListUrlSortField.name)}
            className={classes.wideColumn}
          >
            <FormattedMessage
              defaultMessage="Name"
              description="staff member full name"
            />
          </TableCellHeader>
          <TableCellHeader>
            <FormattedMessage defaultMessage="MFA Status" />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === StaffListUrlSortField.email
                ? getArrowDirection(sort.asc)
                : undefined
            }
            onClick={() => onSort(StaffListUrlSortField.email)}
          >
            <FormattedMessage defaultMessage="Email Address" />
          </TableCellHeader>
          <TableCellHeader>
            <FormattedMessage defaultMessage="User Type" />
          </TableCellHeader>
          <TableCellHeader>
            <FormattedMessage defaultMessage="Status" />
          </TableCellHeader>
        </TableRow>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={
              pageInfo && !disabled ? pageInfo.hasNextPage : undefined
            }
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          staffMembers,
          (staffMember, index) => (
            <TableRow
              className={classNames({
                [classes.tableRow]: !!staffMember
              })}
              hover={!!staffMember}
              onClick={!!staffMember ? onRowClick(staffMember.id) : undefined}
              key={staffMember ? staffMember.id : "skeleton"}
            >
              <TableCell id={`staffRow${index}`}>
                <div className={classes.avatar} id={`avatarRow${index}`}>
                  {maybe(() => staffMember.avatar.url) ? (
                    <img
                      className={classes.avatarImage}
                      src={maybe(() => staffMember.avatar.url)}
                    />
                  ) : (
                    <div className={classes.avatarDefault}>
                      <Typography>{getUserInitials(staffMember)}</Typography>
                    </div>
                  )}
                </div>
                <Typography id={`staffNamerow${index}`}>
                  {getUserName(staffMember) || <Skeleton />}
                </Typography>
                <Typography
                  variant={"caption"}
                  className={classes.statusText}
                  id={`staffStatusRow${index}`}
                >
                  {maybe<React.ReactNode>(
                    () =>
                      staffMember.isActive
                        ? intl.formatMessage({
                            defaultMessage: "Active",
                            description: "staff member status"
                          })
                        : intl.formatMessage({
                            defaultMessage: "Inactive",
                            description: "staff member status"
                          }),
                    <Skeleton />
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                {maybe<React.ReactNode>(() => {
                  if (staffMember.mfaStatus === MfaStatus.VERIFIED) {
                    return (
                      <div
                        className={classNames(
                          classes.statusVerified,
                          classes.statusWrapper
                        )}
                      >
                        <span className={classes.statusVerifiedLabel}>
                          {"Verified"}
                        </span>
                      </div>
                    );
                  } else if (staffMember.mfaStatus === MfaStatus.PENDING) {
                    return (
                      <div
                        className={classNames(
                          classes.statusPending,
                          classes.statusWrapper
                        )}
                      >
                        <span className={classes.statusPendingLabel}>
                          {"Pending"}
                        </span>
                      </div>
                    );
                  } else if (staffMember.mfaStatus === MfaStatus.EXPIRED) {
                    return (
                      <div
                        className={classNames(
                          classes.statusExpired,
                          classes.statusWrapper
                        )}
                      >
                        <span className={classes.statusExpiredLabel}>
                          {"Expired"}
                        </span>
                      </div>
                    );
                  } else if (staffMember.mfaStatus === MfaStatus.INELIGIBLE) {
                    return "";
                  }
                })}
              </TableCell>
              <TableCell id={`staffEmail${index}`}>
                {maybe<React.ReactNode>(() => staffMember.email, <Skeleton />)}
              </TableCell>
              <TableCell id={`staffPermissionRow${index}`}>
                {maybe(
                  () =>
                    getStaffUserType(staffMember).description ||
                    staffMember?.permissionGroups[0]?.name,
                  "-"
                )}
              </TableCell>
              <TableCell id={`statusRow${index}`}>
                {maybe(() => getStaffStatus(staffMember).description, "-")}
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No users found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
StaffList.displayName = "StaffList";
export default StaffList;
